import React from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    title: "1. Acceptance",
    body: `By creating an account or using Rep Fleet ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using Rep Fleet on behalf of a company, you represent that you have authority to bind that company, and "you" refers to both you personally and the company you represent.`,
  },
  {
    title: "2. The Service",
    body: `Rep Fleet is a software-as-a-service platform that helps trucking companies track vehicle maintenance, repairs, fuel, parts, and driver/mechanic activity. Access is provided over the web at https://rep-fleet.com and through Rep Fleet mobile apps.

We may add, change, or remove features at any time. Material reductions in functionality will be announced with reasonable notice.`,
  },
  {
    title: "3. Accounts and access",
    body: `You must provide accurate account information and keep your password confidential. You are responsible for everything that happens under your account.

Company admins can create logins for staff, mechanics, and drivers. Admins are responsible for offboarding users promptly when they leave the company.

We reserve the right to suspend or terminate accounts that violate these Terms, harm other users, or abuse the platform.`,
  },
  {
    title: "4. Your content",
    body: `Content you upload — repair records, invoices, photos, driver info, fuel entries, etc. — belongs to your company. You grant Rep Fleet a limited license to store, process, and display this content solely to operate the Service for you.

You represent that you have the right to upload every piece of content and that it does not violate any third-party rights or law.`,
  },
  {
    title: "5. Acceptable use",
    body: `You agree not to:
• Reverse-engineer, decompile, or resell the Service.
• Use the Service to store illegal content or personal information that isn't necessary for fleet operations.
• Attempt to breach tenant isolation or access another company's data.
• Overload the platform with automated scraping or abusive request patterns.
• Circumvent rate limits, quotas, or subscription tier boundaries.`,
  },
  {
    title: "6. Subscription and billing",
    body: `Some Rep Fleet features require a paid subscription. Pricing and included limits are shown at https://rep-fleet.com/#pricing and inside the app. Fees are billed in advance, non-refundable except where required by law.

You can cancel at any time. Cancellation takes effect at the end of the current billing period.

We may change prices with at least 30 days' notice. Continued use after a price change constitutes acceptance.`,
  },
  {
    title: "7. Free trial and beta features",
    body: `Where a free trial is offered, you may use the Service without charge for the trial period. After the trial ends, your subscription starts unless you cancel.

We may from time to time offer "beta" features. Beta features are provided as-is, may be changed or removed at any time, and are not covered by any SLA.`,
  },
  {
    title: "8. AI-generated output",
    body: `Rep Fleet uses third-party AI providers (currently Google Gemini) to parse invoices and answer questions in the AI Assistant. AI output can be inaccurate, incomplete, or wrong. You are responsible for reviewing and verifying AI-generated content before relying on it — especially cost totals, part numbers, and DOT-related data.

You retain ownership of your inputs. Rep Fleet and its AI providers do not use your business content to train foundation models.`,
  },
  {
    title: "9. Motive and other integrations",
    body: `Where you connect a third-party integration (e.g. Motive telematics), your use of that integration is also governed by that vendor's own terms and privacy policy. Rep Fleet is not responsible for the availability, accuracy, or content of third-party services.`,
  },
  {
    title: "10. Warranty disclaimer",
    body: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. We do not guarantee uninterrupted access, and we do not warrant that AI output will be accurate.`,
  },
  {
    title: "11. Limitation of liability",
    body: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL REP FLEET, ITS AFFILIATES, OR ITS SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.

OUR TOTAL LIABILITY FOR ANY CLAIM RELATED TO THE SERVICE IS LIMITED TO THE GREATER OF $100 OR THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM.`,
  },
  {
    title: "12. Indemnity",
    body: `You agree to indemnify and hold Rep Fleet harmless from any claims, damages, or costs arising out of your content, your use of the Service in violation of these Terms, or your violation of any law or third-party right.`,
  },
  {
    title: "13. Termination",
    body: `You can terminate your account at any time from the Settings page or by writing to support@rep-fleet.com. We can terminate or suspend the Service if you materially breach these Terms.

Upon termination, your right to use the Service ends. We will provide a reasonable window to export your data before deletion — see the Privacy Policy for retention details.`,
  },
  {
    title: "14. Governing law",
    body: `These Terms are governed by the laws of the State of [YOUR-STATE], United States, without regard to its conflict-of-laws provisions. Any dispute will be resolved in the state or federal courts located in [YOUR-COUNTY], and you consent to the jurisdiction of those courts.`,
  },
  {
    title: "15. Changes",
    body: `We may update these Terms from time to time. The "Last updated" date reflects the latest revision. If we make material changes, we will notify you in-app or by email at least 14 days before they take effect. Continued use of the Service after that date constitutes acceptance.`,
  },
  {
    title: "16. Contact",
    body: `Questions about these Terms? Write to support@rep-fleet.com.`,
  },
];

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs font-mono uppercase tracking-widest text-blue-600 hover:text-blue-800" data-testid="terms-back-home">← Back to Rep Fleet</Link>
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.25em] text-blue-600 font-mono font-bold">/// Legal</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2 text-slate-900">Terms of Service</h1>
          <p className="text-sm text-slate-500 font-mono mt-2">Last updated: February 16, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none mt-8 space-y-8">
          <p className="text-base leading-relaxed">
            Welcome to Rep Fleet. These Terms are the agreement between your company and Rep Fleet. Please read them carefully — they cover important topics like subscriptions, acceptable use, and liability.
          </p>

          {SECTIONS.map((s, i) => (
            <section key={i} data-testid={`terms-section-${i}`}>
              <h2 className="font-display text-xl font-black tracking-tight text-slate-900 mb-2">{s.title}</h2>
              <p className="text-base leading-relaxed whitespace-pre-wrap text-slate-700">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between text-xs font-mono text-slate-500">
          <Link to="/privacy" className="hover:text-blue-600" data-testid="terms-to-privacy">← Privacy Policy</Link>
          <span>© 2026 Rep Fleet</span>
        </div>
      </div>
    </div>
  );
}
